import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsDownUp,
  ChevronsUpDown,
  ClipboardCopy,
  Download,
  Eraser,
  ExternalLink,
  FileText,
  Hash,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  Quote,
  Search,
  Sparkles,
  Unlock,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import {
  formatJson,
  minifyJson,
  parseJson,
  type JsonResult,
  type ParseResult,
} from '../lib/json';
import { jsonToYaml } from '../lib/yaml';
import { setToolSeed } from '../lib/seedTransfer';
import { downloadBlob } from '../../lib/blob';
import {
  buildTree,
  computeStats,
  findInTree,
  type JsonNode,
  type JsonStats,
  type SearchHit,
} from '../lib/jsonTree';
import { detectEscapedJson } from '../lib/jsonUnescape';
import {
  allContainerIds,
  JsonTreeView,
} from '../components/JsonTreeView';
import { MonacoPane, type MonacoMarker } from '../components/MonacoPane';
import { ToolHeader } from '../components/ToolHeader';

/**
 * `/tools/format/json` — JSON formatter / tree explorer.
 *
 * Layout (top-down, left-right):
 *
 *   ┌─ ToolHeader ─────────────────────────────────────────────────────┐
 *   │  Slim parse-status + stats line                                  │
 *   ├─────────── 输入 (40%) ──────────┬──────── 树视图 (60%) ─────────┤
 *   │  format/minify · indent · clear │  [search][prev][next] · [展开]│
 *   │                                 │   [折叠] · [复制] · [关闭左栏]  │
 *   │  Monaco JSON editor             │   JsonTreeView                │
 *   │                                 │                               │
 *   │                                 │  (selected ↳ sticky path bar) │
 *   └─────────────────────────────────┴───────────────────────────────┘
 *
 * Why two columns rather than the previous stacked layout: tree view
 * is the work surface 90% of the time. Stacking buried it below an
 * always-visible Monaco pane plus four card-pads of toolbars, leaving
 * the tree with maybe 200–300px of vertical space on a 13" screen.
 * Splitting them releases full-height to each, and a "hide input"
 * button on the right toolbar lets the user push the split to ~100/0
 * when they just want to read.
 *
 * All operation buttons that sit *above* the tree are icon-only with
 * tooltips so the strip stays one row high; the search input is the
 * only labelled control because typing into it is the dominant
 * interaction.
 */

type Action = 'format' | 'minify';

export default function JsonTool(): JSX.Element {
  const [input, setInput] = useState('');
  const [indent, setIndent] = useState<2 | 4>(2);
  const [lastAction, setLastAction] = useState<Action>('format');
  const [showInput, setShowInput] = useState(true);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [selected, setSelected] = useState<JsonNode | null>(null);

  const navigate = useNavigate();

  // Expand / collapse "all" command for JsonTreeView. tick is a
  // monotonic counter; bumping it tells the tree "apply ids now". null
  // means no command issued yet (initial mount uses the tree's own
  // seedExpanded heuristic). See JsonTreeView's expandCommand prop.
  const [expandCommand, setExpandCommand] = useState<
    { tick: number; ids: ReadonlySet<number> } | null
  >(null);

  // Parse → value. Tree wants the value, Monaco wants the string.
  const parsed: ParseResult = useMemo(() => parseJson(input), [input]);

  const formatted: JsonResult | null = useMemo(() => {
    if (input.trim().length === 0) return null;
    return lastAction === 'format' ? formatJson(input, indent) : minifyJson(input);
  }, [input, indent, lastAction]);

  const root: JsonNode | null = useMemo(() => {
    if (!parsed.ok) return null;
    return buildTree(parsed.value);
  }, [parsed]);

  const stats: JsonStats | null = useMemo(() => {
    if (!root) return null;
    return computeStats(root, formatted?.ok ? formatted.output : input);
  }, [root, formatted, input]);

  const hits: SearchHit[] = useMemo(() => {
    if (!root) return [];
    return findInTree(root, query);
  }, [root, query]);

  useEffect(() => {
    setActiveIndex((prev) => {
      if (hits.length === 0) return 0;
      return prev >= hits.length ? 0 : prev;
    });
  }, [hits]);

  const highlightIds = useMemo(() => new Set(hits.map((h) => h.id)), [hits]);
  const activeId = hits.length > 0 ? hits[activeIndex]?.id : undefined;

  const escape = useMemo(() => detectEscapedJson(input), [input]);

  const markers: MonacoMarker[] = useMemo(() => {
    if (parsed.ok || !parsed.line || !parsed.column) return [];
    return [
      {
        line: parsed.line,
        column: parsed.column,
        endColumn: parsed.column + 1,
        message: parsed.error,
        severity: 'error',
      },
    ];
  }, [parsed]);

  const onClear = (): void => {
    setInput('');
    setQuery('');
    setSelected(null);
  };

  const onApplyEscape = (): void => {
    if (escape.isNested && escape.innerPretty) {
      setInput(escape.innerPretty);
    }
  };

  // Expand / collapse "all": ship a fresh command tick to the tree.
  // The tree's effect watches `tick` (not `ids`), so bumping it is the
  // cue to apply the new set; the user's own per-row toggles are not
  // overwritten between bumps. Earlier this was implemented via a
  // mutable `expandedRef` + `key` remount, which silently swallowed
  // both buttons because the remount path re-ran `seedExpanded(root)`
  // and ignored the ref entirely.
  const expandAll = (): void => {
    if (!root) return;
    setExpandCommand((prev) => ({
      tick: (prev?.tick ?? 0) + 1,
      ids: allContainerIds(root),
    }));
  };
  const collapseAll = (): void => {
    if (!root) return;
    setExpandCommand((prev) => ({
      tick: (prev?.tick ?? 0) + 1,
      ids: new Set([root.id]),
    }));
  };

  const copyFormatted = async (): Promise<void> => {
    if (!formatted || !formatted.ok) return;
    try {
      await navigator.clipboard.writeText(formatted.output);
    } catch {
      /* ignore */
    }
  };

  /**
   * Source string for YAML conversion. Prefer the formatted output when
   * available so the YAML carries the same indent / key order the user
   * sees on screen; fall back to raw input only when the formatter
   * couldn't run (which means jsonToYaml will still report the parse
   * error properly via JSON.parse). Empty input → empty source.
   */
  const yamlSource = (): string => {
    if (formatted && formatted.ok) return formatted.output;
    return input;
  };

  const copyAsYaml = async (): Promise<void> => {
    const src = yamlSource();
    if (src.trim().length === 0) return;
    const r = jsonToYaml(src);
    if (!r.ok) return; // Parse error → leave button as a no-op; status badge already surfaces the error.
    try {
      await navigator.clipboard.writeText(r.output);
    } catch {
      /* ignore */
    }
  };

  const downloadAsYaml = (): void => {
    const src = yamlSource();
    if (src.trim().length === 0) return;
    const r = jsonToYaml(src);
    if (!r.ok) return;
    const blob = new Blob([r.output], { type: 'text/plain;charset=utf-8' });
    downloadBlob(blob, 'output.yaml');
  };

  /**
   * Hand off to /tools/format/yaml in JSON → YAML mode, seeding the
   * input. We seed with the formatted output (same reasoning as
   * yamlSource) so the receiving tool starts on what the user sees.
   */
  const openInYamlTool = (): void => {
    const src = yamlSource();
    setToolSeed({
      target: '/tools/format/yaml',
      text: src,
      mode: 'json2yaml',
    });
    navigate('/tools/format/yaml');
  };

  const onPrevHit = (): void => {
    if (hits.length === 0) return;
    setActiveIndex((i) => (i - 1 + hits.length) % hits.length);
  };
  const onNextHit = (): void => {
    if (hits.length === 0) return;
    setActiveIndex((i) => (i + 1) % hits.length);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-4">
      <ToolHeader
        toolId="json"
        category="format"
        description="格式化 / 压缩 / 校验 + 树形预览：节点折叠、类型着色、路径复制、搜索高亮、嵌套字符串自动去转义。全部纯前端，输入不上传。"
      />

      {/* Slim status / stats strip — keeps the workspace tall. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px]">
        <ParseBadge input={input} parsed={parsed} />
        {stats && (
          <>
            <Stat label="键" value={stats.keys} />
            <Stat label="数组" value={stats.arrays} />
            <Stat label="对象" value={stats.objects} />
            <Stat label="叶子" value={stats.leaves} />
            <Stat label="深度" value={stats.maxDepth} />
            <Stat label="字节" value={formatBytes(stats.utf8Bytes)} />
          </>
        )}
        {!parsed.ok && parsed.error && (
          <span className="ml-auto inline-flex items-center gap-1 truncate font-mono text-[11px] text-rose-600 dark:text-rose-400">
            <XCircle className="h-3 w-3 shrink-0" />
            {parsed.line ? `第 ${parsed.line} 行 ${parsed.column} 列：` : ''}
            <span className="truncate">{parsed.error}</span>
          </span>
        )}
      </div>

      {/* Two-column workspace. Stacks on narrow screens.
       *
       * Why grid not flex: with `lg:flex-row` + `align-items: stretch`,
       * sibling cross-axis size is supposed to be the row height.
       * In practice, when one column's child is flex-1 with a CSS
       * min-height (Monaco editor's 240px floor), Safari and some
       * Chrome variants stop honoring stretch and the column collapses
       * to its intrinsic size, leaving the columns at unequal heights.
       *
       * `grid-rows-1` resolves to `repeat(1, minmax(0, 1fr))` which
       * forces a single row of exactly the remaining height; grid's
       * stretch is computed first and is unaffected by descendant
       * min-height. */}
      <div
        className={clsx(
          'grid min-h-0 min-w-0 flex-1 gap-3',
          showInput
            ? 'grid-cols-1 lg:grid-cols-[2fr_3fr] lg:grid-rows-1'
            : 'grid-cols-1 lg:grid-rows-1',
        )}
      >
        {showInput && (
          <InputColumn
            input={input}
            onInputChange={setInput}
            indent={indent}
            onIndentChange={setIndent}
            lastAction={lastAction}
            onActionChange={setLastAction}
            escape={escape}
            onApplyEscape={onApplyEscape}
            onClear={onClear}
            markers={markers}
          />
        )}

        <ResultColumn
          query={query}
          onQueryChange={setQuery}
          hits={hits}
          activeIndex={activeIndex}
          onPrev={onPrevHit}
          onNext={onNextHit}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          onCopyFormatted={copyFormatted}
          onCopyAsYaml={copyAsYaml}
          onDownloadAsYaml={downloadAsYaml}
          onOpenInYamlTool={openInYamlTool}
          canCopy={!!formatted && formatted.ok}
          canConvertToYaml={parsed.ok}
          showInput={showInput}
          onToggleInput={() => setShowInput((v) => !v)}
          root={root}
          highlightIds={highlightIds}
          activeId={activeId}
          expandCommand={expandCommand}
          onSelect={setSelected}
          selected={selected}
          onCloseSelection={() => setSelected(null)}
          inputIsEmpty={input.trim().length === 0}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Left column — input + format controls                                */
/* -------------------------------------------------------------------- */

interface InputColumnProps {
  input: string;
  onInputChange: (v: string) => void;
  indent: 2 | 4;
  onIndentChange: (v: 2 | 4) => void;
  lastAction: Action;
  onActionChange: (a: Action) => void;
  escape: ReturnType<typeof detectEscapedJson>;
  onApplyEscape: () => void;
  onClear: () => void;
  markers: MonacoMarker[];
}

function InputColumn({
  input,
  onInputChange,
  indent,
  onIndentChange,
  lastAction,
  onActionChange,
  escape,
  onApplyEscape,
  onClear,
  markers,
}: InputColumnProps): JSX.Element {
  return (
    <section className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-line bg-bg-card">
      <header className="flex flex-wrap items-center gap-1.5 border-b border-line px-2 py-1.5">
        <div className="inline-flex rounded-md border border-line">
          <ModeButton current={lastAction} value="format" onClick={onActionChange}>
            <Sparkles className="h-3.5 w-3.5" />
            格式化
          </ModeButton>
          <ModeButton current={lastAction} value="minify" onClick={onActionChange}>
            <Minimize2 className="h-3.5 w-3.5" />
            压缩
          </ModeButton>
        </div>
        <select
          className="select py-0.5 text-[11.5px]"
          value={indent}
          onChange={(e) => onIndentChange(Number(e.target.value) as 2 | 4)}
          disabled={lastAction !== 'format'}
          title="缩进空格数（仅格式化时生效）"
        >
          <option value={2}>2 空格</option>
          <option value={4}>4 空格</option>
        </select>

        <div className="ml-auto flex items-center gap-1">
          {escape.isNested && (
            <button
              type="button"
              onClick={onApplyEscape}
              className="inline-flex items-center gap-1 rounded-md border border-amber-400/60 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300"
              title='把字符串里的转义 JSON 一键解开（如 "{\"a\":1}" → {"a":1}）'
            >
              <Unlock className="h-3 w-3" />
              去嵌套
            </button>
          )}
          <IconButton
            icon={Eraser}
            label="清空输入"
            onClick={onClear}
            disabled={input.length === 0}
          />
        </div>
      </header>

      {/* Two-step height: an explicit flex-1 wrapper takes the row's
       * remaining height, then MonacoPane fills 100% of that wrapper.
       * Doing it in one step (flex-1 on MonacoPane directly) is unreliable
       * because Monaco's section also carries `min-height: 240px`, and
       * flex-grow + min-height together is a known browser quirk where
       * the item stops growing past min-height in some Chromium / WebKit
       * versions. */}
      <div className="min-h-0 flex-1">
        <MonacoPane
          title="原文"
          value={input}
          onChange={onInputChange}
          language="json"
          placeholder={'粘贴 JSON 文本…\n例如：{"a":1,"b":[2,3]}'}
          markers={markers}
          actions={['clear', 'format']}
          minHeight={0}
          className="h-full !rounded-none !border-0"
        />
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------- */
/* Right column — tree view + icon toolbar                              */
/* -------------------------------------------------------------------- */

interface ResultColumnProps {
  query: string;
  onQueryChange: (v: string) => void;
  hits: SearchHit[];
  activeIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onCopyFormatted: () => void;
  onCopyAsYaml: () => void;
  onDownloadAsYaml: () => void;
  onOpenInYamlTool: () => void;
  canCopy: boolean;
  canConvertToYaml: boolean;
  showInput: boolean;
  onToggleInput: () => void;
  root: JsonNode | null;
  highlightIds: Set<number>;
  activeId: number | undefined;
  expandCommand: { tick: number; ids: ReadonlySet<number> } | null;
  onSelect: (n: JsonNode) => void;
  selected: JsonNode | null;
  onCloseSelection: () => void;
  inputIsEmpty: boolean;
}

function ResultColumn({
  query,
  onQueryChange,
  hits,
  activeIndex,
  onPrev,
  onNext,
  onExpandAll,
  onCollapseAll,
  onCopyFormatted,
  onCopyAsYaml,
  onDownloadAsYaml,
  onOpenInYamlTool,
  canCopy,
  canConvertToYaml,
  showInput,
  onToggleInput,
  root,
  highlightIds,
  activeId,
  expandCommand,
  onSelect,
  selected,
  onCloseSelection,
  inputIsEmpty,
}: ResultColumnProps): JSX.Element {
  const matchInfo = query
    ? hits.length === 0
      ? '0'
      : `${activeIndex + 1}/${hits.length}`
    : '';
  const hasHits = hits.length > 0;

  return (
    <section className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-line bg-bg-card">
      <header className="flex flex-wrap items-center gap-1 border-b border-line px-2 py-1.5">
        <label className="flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded border border-line bg-bg px-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-ink-dim" />
          <input
            className="min-w-0 flex-1 bg-transparent text-[12px] outline-none disabled:cursor-not-allowed"
            placeholder={root ? '搜索 key 或 value' : '粘贴合法 JSON 后启用搜索'}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (e.shiftKey) onPrev();
                else onNext();
              } else if (e.key === 'Escape') {
                onQueryChange('');
              }
            }}
            spellCheck={false}
          />
          {matchInfo && (
            <span
              className={clsx(
                'shrink-0 font-mono text-[10.5px]',
                hasHits ? 'text-ink-muted' : 'text-rose-500',
              )}
            >
              {matchInfo}
            </span>
          )}
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange('')}
              className="shrink-0 rounded p-0.5 text-ink-dim hover:text-accent"
              title="清空搜索"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </label>
        <div className="flex items-center gap-0.5">
          <IconButton
            icon={ChevronUp}
            label="上一个匹配 (Shift+Enter)"
            onClick={onPrev}
            disabled={!hasHits}
          />
          <IconButton
            icon={ChevronDown}
            label="下一个匹配 (Enter)"
            onClick={onNext}
            disabled={!hasHits}
          />
          <span className="mx-1 h-4 w-px bg-line" aria-hidden />
          <IconButton
            icon={ChevronsUpDown}
            label="全部展开"
            onClick={onExpandAll}
            disabled={!root}
          />
          <IconButton
            icon={ChevronsDownUp}
            label="全部折叠"
            onClick={onCollapseAll}
            disabled={!root}
          />
          <span className="mx-1 h-4 w-px bg-line" aria-hidden />
          <IconButton
            icon={ClipboardCopy}
            label="复制美化结果"
            onClick={onCopyFormatted}
            disabled={!canCopy}
          />
          <span className="mx-1 h-4 w-px bg-line" aria-hidden />
          <IconButton
            icon={FileText}
            label="复制为 YAML"
            onClick={onCopyAsYaml}
            disabled={!canConvertToYaml}
          />
          <IconButton
            icon={Download}
            label="下载为 .yaml 文件"
            onClick={onDownloadAsYaml}
            disabled={!canConvertToYaml}
          />
          <IconButton
            icon={ExternalLink}
            label="在 YAML 工具中打开（JSON → YAML 模式）"
            onClick={onOpenInYamlTool}
            disabled={!canConvertToYaml}
          />
          <span className="mx-1 h-4 w-px bg-line" aria-hidden />
          <IconButton
            icon={showInput ? PanelLeftClose : PanelLeftOpen}
            label={showInput ? '隐藏左侧输入' : '显示左侧输入'}
            onClick={onToggleInput}
          />
        </div>
      </header>

      {/* Body: tree or empty placeholder. */}
      <div className="min-h-0 flex-1 overflow-auto py-1.5">
        {root ? (
          <JsonTreeView
            root={root}
            highlightIds={highlightIds}
            activeId={activeId}
            expandCommand={expandCommand}
            onSelect={onSelect}
            query={query}
          />
        ) : (
          <div className="flex h-full items-center justify-center px-4 text-center text-[12px] text-ink-dim">
            {inputIsEmpty
              ? '— 在左侧贴入 JSON —'
              : '— 解析失败，修正后将显示树视图 —'}
          </div>
        )}
      </div>

      {/* Sticky path bar. Mounts only when something is selected. */}
      {selected && (
        <footer className="flex flex-wrap items-center gap-1.5 border-t border-line bg-bg-soft/40 px-2 py-1.5 text-[12px]">
          <Hash className="h-3 w-3 shrink-0 text-accent" />
          <code className="min-w-0 flex-1 break-all rounded bg-bg-soft px-1.5 py-0.5 font-mono text-ink">
            {selected.path}
          </code>
          <span className="rounded border border-line px-1 text-[10px] uppercase tracking-wide text-ink-dim">
            {labelForType(selected.type)}
          </span>
          <CopyChip
            label="复制路径"
            value={selected.path}
            icon={<Hash className="h-3 w-3" />}
          />
          <CopyChip
            label="复制值"
            value={valueAsString(selected)}
            icon={<Quote className="h-3 w-3" />}
          />
          <button
            type="button"
            onClick={onCloseSelection}
            className="rounded p-0.5 text-ink-dim hover:text-accent"
            title="取消选中"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </footer>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------- */
/* Helpers                                                              */
/* -------------------------------------------------------------------- */

function ParseBadge({
  input,
  parsed,
}: {
  input: string;
  parsed: ParseResult;
}): JSX.Element {
  if (input.length === 0) {
    return <span className="text-ink-dim">— 等待输入 —</span>;
  }
  if (parsed.ok) {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="h-3 w-3" /> 合法 JSON
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400">
      <XCircle className="h-3 w-3" /> 解析失败
    </span>
  );
}

function ModeButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Action;
  value: Action;
  onClick: (a: Action) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 text-[11.5px] font-medium transition first:rounded-l-md last:rounded-r-md',
        active ? 'bg-accent/10 text-accent' : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function IconButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={clsx(
        'inline-flex h-7 w-7 items-center justify-center rounded transition',
        disabled
          ? 'cursor-not-allowed text-ink-dim opacity-50'
          : 'text-ink-muted hover:bg-bg-soft hover:text-accent',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function Stat({ label, value }: { label: string; value: number | string }): JSX.Element {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-ink-dim">{label}</span>
      <span className="font-mono text-ink">{value}</span>
    </span>
  );
}

function CopyChip({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!value}
      className={clsx(
        'inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-[11px]',
        value
          ? 'text-ink-muted hover:border-accent/50 hover:text-accent'
          : 'cursor-not-allowed text-ink-dim',
      )}
      title={label}
    >
      {done ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      ) : (
        icon ?? <ClipboardCopy className="h-3 w-3" />
      )}
      {done ? '已复制' : label}
    </button>
  );
}

function labelForType(type: JsonNode['type']): string {
  switch (type) {
    case 'object':
      return 'OBJECT';
    case 'array':
      return 'ARRAY';
    case 'string':
      return 'STRING';
    case 'number':
      return 'NUMBER';
    case 'boolean':
      return 'BOOL';
    case 'null':
      return 'NULL';
  }
}

function valueAsString(node: JsonNode): string {
  if (node.type === 'string') return String(node.value);
  if (node.type === 'object' || node.type === 'array') {
    try {
      return JSON.stringify(reconstitute(node), null, 2);
    } catch {
      return '';
    }
  }
  if (node.type === 'null') return 'null';
  return String(node.value);
}

function reconstitute(node: JsonNode): unknown {
  if (node.type === 'object') {
    const out: Record<string, unknown> = {};
    for (const c of node.children ?? []) out[c.key] = reconstitute(c);
    return out;
  }
  if (node.type === 'array') {
    return (node.children ?? []).map(reconstitute);
  }
  if (node.type === 'null') return null;
  return node.value;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
